<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter } from 'dashboard/composables/store';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import ReportSectionCard from '../settings/reports/components/ReportSectionCard.vue';
import AnnouncementCarousel from './AnnouncementCarousel.vue';
import ConquestsCard from './ConquestsCard.vue';
import HomeHero from './HomeHero.vue';
import HomeShortcutCard from './HomeShortcutCard.vue';
import {
  HOME_PRIMARY_LINK_CLASS,
  HOME_SECONDARY_LINK_CLASS,
} from './homeTones';

const props = defineProps({
  accountName: {
    type: String,
    default: 'Talkvia',
  },
  operatorName: {
    type: String,
    default: '',
  },
  hasCorbanModule: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();
const { accountScopedRoute } = useAccount();
const currentRole = useMapGetter('getCurrentRole');
const currentUser = useMapGetter('getCurrentUser');
const currentAccount = useMapGetter('getCurrentAccount');

const displayName = computed(
  () => props.operatorName || t('OPERATIONAL_HOME.FALLBACK_NAME')
);

const canManageCorban = computed(
  () =>
    currentUser.value?.type === 'SuperAdmin' ||
    currentRole.value === 'administrator'
);

const canManageHelpCenter = computed(
  () =>
    canManageCorban.value &&
    currentAccount.value?.features?.[FEATURE_FLAGS.HELP_CENTER] === true
);

const shouldConnectCorban = computed(
  () => props.hasCorbanModule && canManageCorban.value
);

const headline = computed(() =>
  props.hasCorbanModule
    ? t('OPERATIONAL_HOME.HERO.CORBAN_PENDING_TITLE', {
        name: displayName.value,
      })
    : t('OPERATIONAL_HOME.HERO.NO_CORBAN_TITLE', { name: displayName.value })
);

const description = computed(() =>
  props.hasCorbanModule
    ? t('OPERATIONAL_HOME.HERO.CORBAN_PENDING_DESCRIPTION')
    : t('OPERATIONAL_HOME.HERO.NO_CORBAN_DESCRIPTION')
);

const primaryAction = computed(() => {
  if (shouldConnectCorban.value) {
    return {
      label: t('OPERATIONAL_HOME.ACTIONS.CONFIGURE_CORBAN'),
      icon: 'i-hugeicons-plug-01',
      to: accountScopedRoute('settings_applications'),
    };
  }

  return {
    label: t('OPERATIONAL_HOME.ACTIONS.OPEN_CONVERSATIONS'),
    icon: 'i-hugeicons-bubble-chat',
    to: accountScopedRoute('talkvia_conversations'),
  };
});

// "Manual e Tour" permanece ao lado de Contatos.
const workspaceCards = computed(() => [
  {
    key: 'conversations',
    title: t('OPERATIONAL_HOME.SHORTCUTS.CONVERSATIONS.TITLE'),
    description: t('OPERATIONAL_HOME.SHORTCUTS.CONVERSATIONS.DESCRIPTION'),
    icon: 'i-hugeicons-bubble-chat',
    tone: 'blue',
    to: accountScopedRoute('talkvia_conversations'),
  },
  {
    key: 'contacts',
    title: t('OPERATIONAL_HOME.SHORTCUTS.CONTACTS.TITLE'),
    description: t('OPERATIONAL_HOME.SHORTCUTS.CONTACTS.DESCRIPTION'),
    icon: 'i-hugeicons-user-group',
    tone: 'teal',
    to: accountScopedRoute('contacts_dashboard_index', {}, { page: 1 }),
  },
  {
    key: 'tour',
    title: t('OPERATIONAL_HOME.SHORTCUTS.TOUR.TITLE'),
    description: t('OPERATIONAL_HOME.SHORTCUTS.TOUR.DESCRIPTION'),
    icon: 'i-hugeicons-book-open-01',
    tone: 'violet',
    to: accountScopedRoute('talkvia_tour'),
  },
  {
    key: 'calendar',
    title: t('OPERATIONAL_HOME.SHORTCUTS.CALENDAR.TITLE'),
    description: t('OPERATIONAL_HOME.SHORTCUTS.CALENDAR.DESCRIPTION'),
    icon: 'i-hugeicons-calendar-03',
    tone: 'amber',
    to: accountScopedRoute('talkvia_calendar'),
  },
]);

const setupItems = computed(() => [
  {
    key: 'inboxes',
    title: t('OPERATIONAL_HOME.SETUP.INBOXES.TITLE'),
    description: t('OPERATIONAL_HOME.SETUP.INBOXES.DESCRIPTION'),
    icon: 'i-hugeicons-inbox',
    to: accountScopedRoute('settings_inbox_list'),
  },
  {
    key: 'integrations',
    title: t('OPERATIONAL_HOME.SETUP.INTEGRATIONS.TITLE'),
    description: t('OPERATIONAL_HOME.SETUP.INTEGRATIONS.DESCRIPTION'),
    icon: 'i-hugeicons-plug-01',
    to: accountScopedRoute('settings_applications'),
    adminOnly: false,
  },
  {
    key: 'team',
    title: t('OPERATIONAL_HOME.SETUP.TEAM.TITLE'),
    description: t('OPERATIONAL_HOME.SETUP.TEAM.DESCRIPTION'),
    icon: 'i-hugeicons-user-settings-01',
    to: accountScopedRoute('agent_list'),
    adminOnly: true,
  },
  {
    key: 'helpCenter',
    title: t('OPERATIONAL_HOME.SETUP.HELP_CENTER.TITLE'),
    description: t('OPERATIONAL_HOME.SETUP.HELP_CENTER.DESCRIPTION'),
    icon: 'i-hugeicons-library',
    to: accountScopedRoute('portals_index', {
      navigationPath: 'portals_articles_index',
    }),
    helpCenterOnly: true,
  },
]);

const visibleSetupItems = computed(() =>
  setupItems.value.filter(
    item =>
      (!item.adminOnly || canManageCorban.value) &&
      (!item.helpCenterOnly || canManageHelpCenter.value)
  )
);
</script>

<template>
  <main class="h-full overflow-y-auto bg-n-background">
    <div class="mx-auto w-full max-w-[90rem] px-4 pb-12 sm:px-6 lg:px-8">
      <HomeHero
        :eyebrow="t('OPERATIONAL_HOME.BREADCRUMB', { account: accountName })"
        :title="headline"
        :description="description"
      >
        <router-link :to="primaryAction.to" :class="HOME_PRIMARY_LINK_CLASS">
          <span class="size-4" :class="primaryAction.icon" />
          {{ primaryAction.label }}
        </router-link>
      </HomeHero>

      <div class="grid gap-5">
        <div class="flex flex-col gap-5 xl:flex-row">
          <AnnouncementCarousel class="xl:flex-[2]" />
          <ConquestsCard
            class="xl:min-w-[20rem] xl:flex-1"
            :user-id="currentUser?.id"
          />
        </div>

        <section :aria-label="t('OPERATIONAL_HOME.SHORTCUTS.TITLE')">
          <div class="mb-3">
            <h2 class="mb-0 text-base font-semibold text-n-slate-12">
              {{ t('OPERATIONAL_HOME.SHORTCUTS.TITLE') }}
            </h2>
            <p class="mb-0 text-xs text-n-slate-11">
              {{ t('OPERATIONAL_HOME.SHORTCUTS.DESCRIPTION') }}
            </p>
          </div>
          <div class="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <HomeShortcutCard
              v-for="card in workspaceCards"
              :key="card.key"
              :title="card.title"
              :description="card.description"
              :icon="card.icon"
              :tone="card.tone"
              :to="card.to"
            />
          </div>
        </section>

        <div class="grid gap-5 xl:grid-cols-3">
          <ReportSectionCard
            class="xl:col-span-2"
            :title="t('OPERATIONAL_HOME.SETUP.TITLE')"
            :description="t('OPERATIONAL_HOME.SETUP.DESCRIPTION')"
            icon="i-hugeicons-task-done-01"
          >
            <div class="grid gap-2 md:grid-cols-2">
              <router-link
                v-for="item in visibleSetupItems"
                :key="item.key"
                :to="item.to"
                class="group flex items-center gap-3 rounded-xl border border-n-weak p-3 transition-colors hover:border-n-brand hover:bg-n-alpha-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand dark:border-n-strong"
              >
                <span
                  class="grid size-9 shrink-0 place-items-center rounded-lg bg-n-solid-2 text-n-slate-11"
                  aria-hidden="true"
                >
                  <span class="size-4" :class="item.icon" />
                </span>
                <span class="min-w-0 flex-1">
                  <span class="block text-sm font-semibold text-n-slate-12">
                    {{ item.title }}
                  </span>
                  <span
                    class="mt-0.5 line-clamp-2 block text-xs leading-5 text-n-slate-11"
                  >
                    {{ item.description }}
                  </span>
                </span>
                <span
                  class="i-hugeicons-arrow-right-01 size-4 shrink-0 text-n-slate-10 transition-colors group-hover:text-n-brand"
                  aria-hidden="true"
                />
              </router-link>
            </div>
          </ReportSectionCard>

          <ReportSectionCard
            :title="t('OPERATIONAL_HOME.NEXT_ACTION.TITLE')"
            :description="t('OPERATIONAL_HOME.NEXT_ACTION.SUBTITLE_ACCOUNT')"
            icon="i-hugeicons-idea-01"
          >
            <h3 class="mb-1.5 text-base font-semibold text-n-slate-12">
              {{
                shouldConnectCorban
                  ? t('OPERATIONAL_HOME.NEXT_ACTION.CORBAN_TITLE')
                  : t('OPERATIONAL_HOME.NEXT_ACTION.CHANNELS_TITLE')
              }}
            </h3>
            <p class="mb-4 text-sm leading-6 text-n-slate-11">
              {{
                shouldConnectCorban
                  ? t('OPERATIONAL_HOME.NEXT_ACTION.CORBAN_BODY')
                  : t('OPERATIONAL_HOME.NEXT_ACTION.CHANNELS_BODY')
              }}
            </p>
            <router-link
              :to="primaryAction.to"
              :class="HOME_SECONDARY_LINK_CLASS"
            >
              {{ primaryAction.label }}
              <span class="i-hugeicons-arrow-right-01 size-4" />
            </router-link>
          </ReportSectionCard>
        </div>
      </div>
    </div>
  </main>
</template>
